import React from 'react';
import {
    ChallengeMemberDisplaySettingsUpdateRequestDto,
    I18NText
} from '@api/ChallengeClient';
import { FieldLabel } from '@components/textfield';
import { Button } from '@components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@components/ui/dialog';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { Textarea } from '@components/ui/textarea';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@components/ui/tooltip';
import { I18n } from '@core/i18n';
import { cn } from '@core/utils/cn';
import { toDataUri } from './role-display';
import { roleIconPresets } from './role-icon-presets';
import { RoleLabelField } from './role-label-field';
import { ChallengeLeaderboardEntry } from './types';

interface EditDisplaySettingsDialogProps {
    entry: ChallengeLeaderboardEntry | null;
    loading?: boolean;
    onClose(): void;
    onSubmit(
        subscriptionId: number,
        settings: ChallengeMemberDisplaySettingsUpdateRequestDto
    ): Promise<void>;
}

const isEmpty = (value?: I18NText) =>
    !value || Object.values(value).every((entry) => !entry);

export function EditDisplaySettingsDialog({
    entry,
    loading,
    onClose,
    onSubmit
}: EditDisplaySettingsDialogProps) {
    const [roleLabel, setRoleLabel] = React.useState<I18NText>({});
    const [roleIconSvg, setRoleIconSvg] = React.useState('');
    const [pinned, setPinned] = React.useState(false);
    const svgId = React.useId();

    React.useEffect(() => {
        setRoleLabel(entry?.roleLabel ?? {});
        setRoleIconSvg(entry?.roleIconSvg ?? '');
        setPinned(entry?.pinned ?? false);
    }, [entry]);

    const selectPreset = (presetKey: string) => {
        const preset = roleIconPresets.find(({ key }) => key === presetKey);

        if (!preset) {
            return;
        }

        setRoleIconSvg(preset.svg);

        if (isEmpty(roleLabel)) {
            setRoleLabel(preset.label);
        }
    };

    return (
        <Dialog
            open={Boolean(entry)}
            onOpenChange={(next) => !next && onClose()}
        >
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        Display settings — {entry?.userName}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-col gap-6">
                    <RoleLabelField value={roleLabel} onChange={setRoleLabel} />

                    <div className="flex flex-col gap-2">
                        <p className="text-sm font-medium">Role icon</p>
                        <div className="mb-2 grid grid-cols-8 gap-2">
                            {roleIconPresets.map((preset) => {
                                const selected = preset.svg === roleIconSvg;

                                return (
                                    <Tooltip key={preset.key}>
                                        <TooltipTrigger asChild>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    selectPreset(preset.key)
                                                }
                                                className={cn(
                                                    'flex items-center justify-center rounded-md border-2 p-1.5',
                                                    selected
                                                        ? 'border-primary'
                                                        : 'border-transparent hover:bg-accent'
                                                )}
                                            >
                                                <img
                                                    src={toDataUri(preset.svg)}
                                                    alt={preset.key}
                                                    width={28}
                                                    height={28}
                                                />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {preset.label[
                                                I18n.mainLanguageKey
                                            ] ?? preset.key}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <FieldLabel htmlFor={svgId} label="Custom SVG" />
                            <Textarea
                                id={svgId}
                                value={roleIconSvg}
                                onChange={(event) =>
                                    setRoleIconSvg(event.target.value)
                                }
                                placeholder="<svg …>"
                                rows={3}
                                className="max-h-48 font-mono text-xs"
                            />
                            <p className="text-xs text-muted-foreground">
                                Pick a preset above, or paste your own SVG.
                                Clear the field to remove the icon.
                            </p>
                        </div>

                        {roleIconSvg.trim() && (
                            <div className="mt-2 flex items-center gap-4 rounded-md bg-[#121212] p-3">
                                <div className="flex flex-col items-center gap-1">
                                    <img
                                        src={toDataUri(roleIconSvg)}
                                        alt="Role icon at leaderboard size"
                                        width={20}
                                        height={20}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        20px
                                    </span>
                                </div>
                                <div className="flex flex-col items-center gap-1">
                                    <img
                                        src={toDataUri(roleIconSvg)}
                                        alt="Role icon enlarged"
                                        width={48}
                                        height={48}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                        48px
                                    </span>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                    Shown at leaderboard size and enlarged. A
                                    blank preview means the SVG is invalid.
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            id="pinned"
                            checked={pinned}
                            onCheckedChange={setPinned}
                        />
                        <Label htmlFor="pinned">
                            Pinned to the top of the leaderboard
                        </Label>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={!entry}
                        loading={loading}
                        onClick={() => {
                            if (!entry) {
                                return;
                            }

                            onSubmit(entry.subscriptionId, {
                                roleLabel: isEmpty(roleLabel)
                                    ? undefined
                                    : roleLabel,
                                roleIconSvg: roleIconSvg.trim() || undefined,
                                pinned
                            }).then(onClose);
                        }}
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
